import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuthStore } from '../../store/authStore'
import { useRulesStore } from '../../store/rulesStore'
import Button from '../common/Button'
import RulesScreen from './RulesScreen'

/**
 * The practice page's safety net for «قوانین طوطی» (R13). The rules are
 * normally accepted once, before anything else of the app (`RulesGate` in
 * App.jsx); this catches a learner that gate let through — the status could
 * not be read at app open — and does not open the microphone without the
 * acceptance while the server posts every scored take (`features.autoPost`).
 * Same store, same screen: accepting here counts for the gate too.
 *
 * Off (`enabled` false) this renders its children and reads nothing.
 */
export default function FeedConsentGate({ enabled, children }) {
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const { phase, consentGiven, text, version, busy, failed, load, accept } = useRulesStore()

  useEffect(() => {
    if (enabled && userId) void load(userId)
  }, [enabled, userId, load])

  if (!enabled || (phase === 'ready' && consentGiven)) return children
  return (
    <ConsentCard
      state={{ phase: phase === 'ready' ? 'ask' : phase === 'error' ? 'error' : 'loading', text, version, busy, failed }}
      onAgree={() => void accept()}
      onRetry={() => userId && void load(userId)}
    />
  )
}

/** The safety net's face: loading, the rules (compact, inside the recording card), or a retry. */
export function ConsentCard({ state, onAgree = () => {}, onRetry = () => {} }) {
  const { t } = useTranslation('social')
  if (state.phase === 'loading') return <p className="text-sm text-ink-2">{t('autoPost.gateLoading')}</p>
  if (state.phase === 'error') {
    return (
      <div className="space-y-3 rounded-xl bg-bad-tint p-4">
        <p className="text-sm text-bad-ink">{t('autoPost.gateError')}</p>
        <Button variant="secondary" onClick={onRetry}>
          {t('autoPost.gateRetry')}
        </Button>
      </div>
    )
  }
  return (
    <div data-consent-gate>
      <RulesScreen compact text={state.text} version={state.version} busy={state.busy} failed={state.failed} onAccept={onAgree} />
    </div>
  )
}
